<template>
  <div class="container-fluid py-4">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2 class="mb-0">Reportes de Empresas</h2>
        <p class="text-muted mb-0">Estadisticas de empresas registradas en el sistema</p>
      </div>
      <button class="btn btn-outline-primary btn-sm" @click="cargarDatos" :disabled="cargando">
        <i class="bi bi-arrow-clockwise"></i> Actualizar
      </button>
    </div>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>

    <!-- KPIs -->
    <div class="row mb-4">
      <div class="col-md-3" v-for="kpi in kpisLista" :key="kpi.label">
        <div class="card text-center">
          <div class="card-body">
            <i :class="kpi.icono" class="fs-1"></i>
            <h4>{{ kpi.valor }}</h4>
            <p class="mb-0">{{ kpi.label }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="row mb-4">
      <!-- Registros por mes -->
      <div class="col-md-6">
        <div class="card h-100">
          <div class="card-header"><h5 class="mb-0">Registros por mes (12 meses)</h5></div>
          <div class="card-body">
            <p v-if="!registrosMes.length" class="text-muted text-center py-3">Sin registros en los ultimos 12 meses</p>
            <div v-else>
              <div v-for="fila in registrosMes" :key="fila.mes" class="mb-2">
                <div class="d-flex justify-content-between small">
                  <span>{{ fila.mes }}</span>
                  <strong>{{ fila.total }}</strong>
                </div>
                <div class="progress" style="height: 8px;">
                  <div class="progress-bar" :style="{ width: porcentajeMes(fila) + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Por plan y por tipo -->
      <div class="col-md-6">
        <div class="card mb-3">
          <div class="card-header"><h5 class="mb-0">Suscripciones activas por plan</h5></div>
          <div class="card-body">
            <div v-for="plan in porPlan" :key="plan.nombre" class="d-flex justify-content-between align-items-center mb-2">
              <span>
                <span class="badge me-2" :style="{ background: plan.color_ui }">&nbsp;</span>
                {{ plan.nombre }}
              </span>
              <strong>{{ plan.total }}</strong>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="card-header"><h5 class="mb-0">Por tipo de empresa</h5></div>
          <div class="card-body">
            <p v-if="!porTipo.length" class="text-muted text-center mb-0">Sin datos</p>
            <div v-for="tipo in porTipo" :key="tipo.tipo" class="d-flex justify-content-between mb-2">
              <span class="text-capitalize">{{ tipo.tipo || 'Sin tipo' }}</span>
              <strong>{{ tipo.total }}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Detalle -->
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">Detalle de empresas</h5>
        <input
          v-model="busqueda"
          type="text"
          class="form-control form-control-sm w-auto"
          placeholder="Buscar por RUC o razon social"
        >
      </div>
      <div class="card-body">
        <p v-if="!detalleFiltrado.length" class="text-muted text-center py-3">Sin empresas registradas</p>
        <div v-else class="table-responsive">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Plan</th>
                <th>Suscripcion</th>
                <th>Vence</th>
                <th>Usuarios</th>
                <th>Registro</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="emp in detalleFiltrado" :key="emp.ruc">
                <td>
                  <strong>{{ emp.razon_social }}</strong><br>
                  <small class="text-muted">{{ emp.ruc }}</small>
                </td>
                <td>{{ emp.plan }}</td>
                <td>{{ emp.estado_suscripcion || '-' }}</td>
                <td>{{ emp.fecha_fin || '-' }}</td>
                <td>{{ emp.usuarios }}</td>
                <td>{{ emp.fecha_registro }}</td>
                <td>
                  <span class="badge" :class="estadoBadge(emp.status)">
                    {{ estadoTexto(emp.status) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import themeMixin from '@/mixins/themeMixin'

export default {
  mixins: [themeMixin],
  name: 'ReportesEmpresas',
  data() {
    return {
      cargando: false,
      error: null,
      busqueda: '',
      kpis: {},
      registrosMes: [],
      porPlan: [],
      porTipo: [],
      detalle: []
    }
  },
  computed: {
    kpisLista() {
      return [
        { label: 'Total', valor: this.kpis.total ?? '-', icono: 'bi bi-buildings text-primary' },
        { label: 'Activas', valor: this.kpis.activas ?? '-', icono: 'bi bi-check-circle text-success' },
        { label: 'Suspendidas', valor: this.kpis.suspendidas ?? '-', icono: 'bi bi-pause-circle text-warning' },
        { label: 'Cerradas', valor: this.kpis.cerradas ?? '-', icono: 'bi bi-x-circle text-danger' }
      ]
    },
    detalleFiltrado() {
      const q = this.busqueda.trim().toLowerCase()
      if (!q) return this.detalle
      return this.detalle.filter(e =>
        (e.razon_social || '').toLowerCase().includes(q) || (e.ruc || '').includes(q)
      )
    }
  },
  mounted() {
    this.cargarDatos()
  },
  methods: {
    async cargarDatos() {
      this.cargando = true
      this.error = null
      try {
        const res = await api.get('/admin/reportes/empresas/resumen')
        const d = res.data?.datos || {}
        this.kpis = d.kpis || {}
        this.registrosMes = d.registros_mes || []
        this.porPlan = d.por_plan || []
        this.porTipo = d.por_tipo || []
        this.detalle = d.detalle || []
      } catch (e) {
        this.error = 'Error al cargar el reporte: ' + (e.response?.data?.error || e.message)
      } finally {
        this.cargando = false
      }
    },
    porcentajeMes(fila) {
      const max = Math.max(...this.registrosMes.map(f => parseInt(f.total) || 0), 1)
      return Math.round((parseInt(fila.total) / max) * 100)
    },
    estadoTexto(status) {
      const m = { 0: 'Suspendida', 1: 'Activa', 2: 'Cerrada' }
      return m[status] ?? 'Desconocido'
    },
    estadoBadge(status) {
      const m = { 0: 'bg-warning', 1: 'bg-success', 2: 'bg-danger' }
      return m[status] ?? 'bg-secondary'
    }
  }
}
</script>

<style scoped>
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}
</style>
